import type { FC } from 'react'

import type { SxProps, Theme } from '@mui/material'
import { Box, Typography } from '@mui/material'

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

interface MobileScreenshotFallbackProps {
  readonly headingId: string
  readonly title: string
  readonly subtitle: string
  readonly imageSrc: string
  readonly imageAlt: string
  readonly maxWidth?: number | undefined
}

export const MobileScreenshotFallback: FC<MobileScreenshotFallbackProps> = ({
  headingId,
  title,
  subtitle,
  imageSrc,
  imageAlt,
  maxWidth,
}) => {
  const containerMaxWidth = maxWidth ?? 1280

  return (
    <Box component="section" role="region" aria-labelledby={headingId} sx={sectionContainerSx}>
      <Typography component="h2" id={headingId} sx={titleSx}>
        {title}
      </Typography>
      <Typography sx={subtitleSx}>{subtitle}</Typography>

      <Box sx={buildScreenshotContainerSx(containerMaxWidth)}>
        <Box
          component="img"
          src={imageSrc}
          alt={imageAlt}
          loading="lazy"
          decoding="async"
          sx={screenshotImageSx}
        />
      </Box>
    </Box>
  )
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const sectionContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
  py: { xs: 4, md: 6 },
  px: { xs: 2, md: 4 },
}

const titleSx: SxProps<Theme> = {
  color: 'text.primary',
  fontSize: { xs: '1.5rem', md: '1.75rem' },
  fontWeight: 600,
  textAlign: 'center',
  mb: 1,
}

const subtitleSx: SxProps<Theme> = {
  color: 'text.secondary',
  fontSize: { xs: '1rem', md: '1.1rem' },
  fontWeight: 400,
  textAlign: 'center',
}

const buildScreenshotContainerSx =
  (maxWidth: number): SxProps<Theme> =>
  (theme) => ({
    maxWidth,
    width: '100%',
    mt: { xs: 4, md: 6 },
    borderRadius: 2,
    border:
      theme.palette.mode === 'dark'
        ? '1px solid rgba(255, 255, 255, 0.08)'
        : '1px solid rgba(62, 39, 35, 0.10)',
    boxShadow:
      theme.palette.mode === 'dark'
        ? '0 4px 24px rgba(0, 0, 0, 0.3)'
        : '0 4px 24px rgba(0, 0, 0, 0.08)',
    overflow: 'hidden',
  })

const screenshotImageSx: SxProps<Theme> = {
  width: '100%',
  height: 'auto',
  display: 'block',
}
